import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { PrivacyPolicy, TermsOfService, CookiePolicy, Imprint, DataProtection } from './pages/LegalPage';
import VerifyEmail from './pages/VerifyEmail';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import StaffDashboard from './pages/StaffDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CandidateProfileView from './components/CandidateProfileView';
import RecruitingPartnerDashboard from './pages/RecruitingPartnerDashboard';
import SourcingPartnerDashboard from './pages/SourcingPartnerDashboard';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ToastProvider } from './context/ToastContext';
import Healthcare from './pages/careers/Healthcare';
import TechnicalIndustrial from './pages/careers/TechnicalIndustrial';
import LogisticsTransport from './pages/careers/LogisticsTransport';
import VocationalPrograms from './pages/careers/VocationalPrograms';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

function AppContent() {
  const { user, isImpersonating, revertImpersonation } = useAuth();

  return (
    <div className="App flex flex-col min-h-screen">
      {isImpersonating && (
        <div className="bg-amber-400 text-amber-900 px-4 py-2 sticky top-0 z-[100] flex justify-between items-center shadow-md font-medium text-sm">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>You are currently impersonating <strong>{user?.name}</strong>.</span>
          </div>
          <button
            onClick={revertImpersonation}
            className="px-4 py-1.5 bg-amber-900 text-amber-50 rounded-lg hover:bg-amber-800 transition-colors shadow-sm font-bold text-xs uppercase tracking-wider"
          >
            Return to Admin
          </button>
        </div>
      )}
      <div className="flex-1 flex flex-col relative">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/careers/healthcare" element={<Healthcare />} />
          <Route path="/careers/technical-industrial" element={<TechnicalIndustrial />} />
          <Route path="/careers/logistics-transport" element={<LogisticsTransport />} />
          <Route path="/careers/vocational-programs" element={<VocationalPrograms />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/imprint" element={<Imprint />} />
          <Route path="/data-protection" element={<DataProtection />} />


          <Route
            path="/student/*"
            element={
              <PrivateRoute allowedRoles={['candidate']}>
                <StudentDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/*"
            element={
              <PrivateRoute allowedRoles={['teacher']}>
                <TeacherDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/staff/*"
            element={
              <PrivateRoute allowedRoles={['staff']}>
                <StaffDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/*"
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/profile/:userId"
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <CandidateProfileView isPageView={true} />
              </PrivateRoute>
            }
          />
          <Route
            path="/recruiting/*"
            element={
              <PrivateRoute allowedRoles={['recruiting_partner']}>
                <RecruitingPartnerDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/sourcing/*"
            element={
              <PrivateRoute allowedRoles={['sourcing_partner']}>
                <SourcingPartnerDashboard />
              </PrivateRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </ToastProvider>
    </LanguageProvider>
  );
}

export default App;
